import React, { useState, useEffect } from "react";
import "./AddMenuModal.css";
import CropModal from "./CropModal";
import { toast } from "react-toastify";
import { BACKEND_URL } from "../../../services/Helper";

const AddMenuModal = ({ onClose, onMenuAdded, onMenuUpdated, editingMenu }) => {
  const [formData, setFormData] = useState({
    itemName: "",
    itemPrice: "",
    prepTime: "",
    availability: "Available",
    itemdescription: "",
    itemImage: null, // blob or url
  });

  const [selectedImage, setSelectedImage] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // 👇 Pre-fill values if editing
  useEffect(() => {
    if (editingMenu) {
      setFormData({
        itemName: editingMenu.itemName || "",
        itemPrice: editingMenu.itemPrice || "",
        prepTime: editingMenu.prepTime || "",
        availability: editingMenu.availability || "Available",
        itemdescription: editingMenu.itemdescription || "",
        itemImage: editingMenu.itemImage || null, // keep existing URL
      });
    }
  }, [editingMenu]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "itemImage" && files[0]) {
      const imageUrl = URL.createObjectURL(files[0]);
      setSelectedImage(imageUrl);
      setShowCropModal(true);
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleCroppedImage = (croppedImgBlob) => {
    setFormData({ ...formData, itemImage: croppedImgBlob });
  };

  // Upload to Cloudinary
  const uploadImageToCloudinary = async (imageBlob) => {
    const cloud = process.env.REACT_APP_CLOUDINARY_CLOUD;
    const preset = process.env.REACT_APP_CLOUDINARY_PRESET;
    const data = new FormData();
    data.append("file", imageBlob);
    data.append("upload_preset", preset);
    data.append("cloud_name", cloud);

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloud}/image/upload`,
      { method: "POST", body: data }
    );

    if (!response.ok) throw new Error("Cloudinary upload failed");
    return response.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const token = localStorage.getItem("shoplogintoken");

      let imageUrl = formData.itemImage;

      // if new blob uploaded → push to Cloudinary
      if (formData.itemImage instanceof Blob) {
        const uploadRes = await uploadImageToCloudinary(formData.itemImage);
        imageUrl = uploadRes.secure_url;
      }

      const payload = {
        itemName: formData.itemName,
        itemPrice: formData.itemPrice,
        prepTime: formData.prepTime,
        availability: formData.availability,
        itemdescription: formData.itemdescription,
        itemImage: imageUrl,
      };

      let url, method;
      if (editingMenu) {
        url = `${BACKEND_URL}/nearbe/updatemenu/${editingMenu._id}`;
        method = "PUT";
      } else {
        url = `${BACKEND_URL}/nearbe/createmenu`;
        method = "POST";
      }

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (!res.ok) {
        toast.error(result.message || "Something went wrong!");
        return;
      }

      if (editingMenu) {
        toast.success("Menu Item Updated Successfully!");
        if (onMenuUpdated) onMenuUpdated(result.updatedMenu || payload);
      } else {
        toast.success("Menu Item Added Successfully!");
        if (onMenuAdded) onMenuAdded(result.newMenu || payload);
      }

      onClose();
    } catch (err) {
      console.error("Error submitting menu item:", err);
      toast.error("Something went wrong, please try again!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <h2>{editingMenu ? "Edit Menu Item" : "Add Menu Item"}</h2>
        <form onSubmit={handleSubmit}>
          <div className="addmenu-form-row">
            <input
              type="text"
              name="itemName"
              placeholder="Item Name"
              value={formData.itemName}
              onChange={handleChange}
              required
            />
            <input
              type="number"
              name="itemPrice"
              placeholder="Item Price"
              value={formData.itemPrice}
              onChange={handleChange}
              required
            />
          </div>

          <div className="addmenu-form-row">
            <input
              type="text"
              name="prepTime"
              placeholder="Preparation Time (e.g., 15 mins)"
              value={formData.prepTime}
              onChange={handleChange}
              required
            />
            <select
              name="availability"
              value={formData.availability}
              onChange={handleChange}
            >
              <option value="Available">Available</option>
              <option value="Out of Stock">Out of Stock</option>
            </select>
          </div>
          <div className="addmenu-form-row">
            <input
              type="text"
              name="itemdescription"
              value={formData.itemdescription}
              onChange={handleChange}
              placeholder="Item Description"
              required
            />
          </div>
          <div className="addmenu-form-row">
            <input
              type="file"
              name="itemImage"
              onChange={handleChange}
              accept="image/*"
            />
          </div>

          <div className="modal-actions">
            <button type="submit" className="addmenu-submit-btn" disabled={loading}>
              {loading
                ? editingMenu
                  ? "Updating..."
                  : "Uploading..."
                : editingMenu
                ? "Update Item"
                : "Add Item"}
            </button>
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>

      {showCropModal && (
        <CropModal
          image={selectedImage}
          onClose={() => setShowCropModal(false)}
          onCropDone={handleCroppedImage}
        />
      )}
    </div>
  );
};

export default AddMenuModal;
